import { UsersAppView } from '../view/UsersAppView';
import { templateUsersApp } from '../view/template';
import { UsersAppRepository } from '../repository/UsersAppRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { UsersAppWebComponent } from '..';
import { ColumnKeys, UsersAppWebComponentSchema } from '../types';
import {
  getSideNavigationPanelSmartViewsListener,
  getSmartViewsListeners,
} from '../../../grid/genericGridSmartViews/utils';
import { GridEvents } from '../../../grid/types/common';
import { UsersSideMenuWebComponent } from '../../sideMenus/usersSideMenu';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';

import { createActionDialog, renderDialogText, RiskManagementSideMenuEvents } from '../../sideMenus/common/common';
import { translateFn } from '../../../utils/translator.helper';
import {
  handleChangeStateBulkSelectionChanged,
  handleChangeWatchlistBulkSelectionChanged,
  handleComplexEnumFilter,
  handleSelectionChange,
  updateWatchlist,
  handleScan,
  handleRowsLoadingFinishedEvent,
  handlePivotFromExtraDetailsPanelToIncidents,
  handlePivotFromExtraDetailsPanelToAdvancedSearch,
  overlayOpenListener,
  buildCategoriesSideMenu,
  processAffectedDeviceData,
} from '../../common/utils';
import {
  ActionsEvents,
  FULFILLED_PROMISE_STATUS,
  Actions,
  InlineMenuExtraDetailsSelectEvents,
  SelectionEvents,
} from '../../common/constants';
import { ColumnKeys as UserBehaviorRisksColumnKeys } from '../../userBehaviorRisksApp/types/index';
import { WEB_COMPONENT_USERS_SIDE_MENU } from '../../../webcomponents.constants';
import { DropDownSelectEvents } from '../../sideMenus/usersSideMenu/types';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';
import { ButtonEvents, GzButtonState } from '../../../ui/gzButton/types';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { ShowExtraDataEventDetails } from '../../sideMenus/components/userDetails/types/types';
import { ContextMenuOverlayEvents } from '../../../ui/gzContextMenuOverlay/component/gzContextMenuOverlay';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { removeActiveStateFromGzIconFromAffectedDevices } from '../../common/customRenderers';

export class UsersAppController extends BaseWebComponentController {
  public component: UsersAppWebComponent;
  public dataRepository: UsersAppRepository;
  public view: UsersAppView;
  public viewConstructor: typeof UsersAppView;
  public dataRepositoryConstructor: typeof UsersAppRepository;

  constructor(component: UsersAppWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: UsersAppView,
      viewTemplate: templateUsersApp,
      dataRepositoryConstructor: UsersAppRepository,
    });
  }

  async getDataForView(): Promise<UsersAppWebComponentSchema> {
    return {
      data: await this.dataRepository.getDataForView(),
      externalFilters: this.component.externalFilters,
    };
  }

  public computeEventListeners() {
    return [
      ...getSmartViewsListeners(
        this.view.dom.$grid,
        this.view.dom.$gridSmartViews,
        this.view.buildGrid.bind(this.view),
        this.view.getTranslations()
      ),
      ...getSideNavigationPanelSmartViewsListener(this.view.dom.$pushBodyLayout, this.view.dom.$gridSmartViews),
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: handleRowsLoadingFinishedEvent.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.VIEW_USERS_BY_RISK,
        callback: this.handleViewOtherUsersByRiskEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.VIEW_HUMAN_RISKS_BY_RISKS,
        callback: this.handleViewHumanRisksByRisksEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.VIEW_HUMAN_RISKS_BY_USERS,
        callback: this.handleViewHumanRisksByUsersEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: DropDownSelectEvents.viewIncidents,
        callback: this.handleDropDownViewIncidentsEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: DropDownSelectEvents.viewEvents,
        callback: this.handleDropDownViewEventsEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.VIEW_INCIDENTS_BY_USER,
        callback: this.handleViewIncidentsByUser,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.VIEW_EVENTS_AND_ALERTS_BY_USER,
        callback: this.handleViewEventsByUser,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$grid,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.IGNORE_USERS,
        callback: this.handleIgnoreUsersEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.RESTORE_USERS,
        callback: this.handleRestoreUsersEvent,
      },
      {
        element: this.view.dom.$grid,
        event: RiskManagementSideMenuEvents.IGNORE_USERS,
        callback: this.handleIgnoreUsersEvent,
      },
      {
        element: this.view.dom.$grid,
        event: RiskManagementSideMenuEvents.RESTORE_USERS,
        callback: this.handleRestoreUsersEvent,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EXPORT_CSV,
        callback: this.handleCSVExportEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.USER_DETAILS_SHOW_EXTRA_DATA,
        callback: this.handleUserDetailsShowExtraDataEvent,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: handleSelectionChange.bind(this),
      },
      {
        element: this.view.dom.changeStateBulk.$select,
        event: SelectEvents.SELECTION_CHANGED,
        callback: handleChangeStateBulkSelectionChanged.bind(this),
      },
      {
        element: this.view.dom.changeWatchlistBulk.$select,
        event: SelectEvents.SELECTION_CHANGED,
        callback: handleChangeWatchlistBulkSelectionChanged.bind(this),
      },
      {
        element: this.view.dom.$scanEmptyStateButton,
        event: ButtonEvents.CLICK,
        callback: handleScan.bind(this),
      },
      {
        element: this.view.dom.scanButton.$button,
        event: ButtonEvents.CLICK,
        callback: handleScan.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: InlineMenuExtraDetailsSelectEvents.viewIncidents,
        callback: handlePivotFromExtraDetailsPanelToIncidents.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: InlineMenuExtraDetailsSelectEvents.viewEvents,
        callback: handlePivotFromExtraDetailsPanelToAdvancedSearch.bind(this),
      },
      {
        // This was needed in order to modify the pointer-events attribute on body
        // It was being set on none at overlay opening and was preventing
        // automated tests to click on other elements while the overlay was open
        element: this.view.dom.$overlay,
        event: ContextMenuOverlayEvents.OPEN,
        callback: overlayOpenListener,
      },
      {
        element: this.view.dom.$grid,
        event: SelectionEvents.RESOURCE_ICON_CLICK,
        callback: this.handleAffectedResourceIconClickedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: SideMenuEvents.CLOSE,
        callback: this.handleRightSideNavClosed,
      },
    ];
  }

  async handleRowClicked(event: CustomEvent) {
    if (!event.detail.rowDetails) {
      // header was clicked, so we do nothing
      return;
    }

    const component = new UsersSideMenuWebComponent();
    component.item = event.detail.rowDetails;
    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();

    const { devicesDetails, endpointIdsFromDevices, uniqueIndicatorsFromDevices } =
      this.getDetailsFromDevices(component);

    const { deviceRemoteDetails, indicatorsRemoteDetails } = await this.getRemoteExtraDetailsForSidePanel(
      component,
      endpointIdsFromDevices,
      uniqueIndicatorsFromDevices
    );

    if (deviceRemoteDetails.length === 0 && indicatorsRemoteDetails.length === 0) {
      component.devicesDetails = [];
      component.indicatorsDetails = [];
      return;
    }

    devicesDetails.map(item => {
      item.name = deviceRemoteDetails[item.endpointId] ?? translateFn('N/A');
    });

    component.devicesDetails = devicesDetails;

    const allIndicatorsWithDetails: any = [];

    for (const [indicatorId, allOccurrences] of Object.entries(uniqueIndicatorsFromDevices)) {
      allIndicatorsWithDetails.push({
        id: indicatorId,
        name: Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
          indicatorId,
          true
        ),
        action: Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineAction(
          indicatorId,
          true
        ),
        description:
          Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineDescription(
            indicatorId,
            true
          ),
        score: indicatorsRemoteDetails[indicatorId]?.scoreBreakdown?.finalScore,
        scoreBreakdown: indicatorsRemoteDetails[indicatorId]?.scoreBreakdown,
        status: indicatorsRemoteDetails[indicatorId]?.status,
        state: indicatorsRemoteDetails[indicatorId]?.state,
        extraDetails: indicatorsRemoteDetails[indicatorId]?.extraDetails,
        allOccurrences,
      });
    }

    component.indicatorsDetails = allIndicatorsWithDetails;
  }

  /**
   *
   * @param component
   * @private
   */
  private getDetailsFromDevices(component) {
    const devicesDetails: any = [];
    const endpointIdsFromDevices: any = [];

    const uniqueIndicatorsFromDevices = {};
    if (Array.isArray(component.item.devices)) {
      component.item.devices.map(device => {
        if (!Array.isArray(device.items)) {
          return;
        }

        device.items.map(item => {
          endpointIdsFromDevices.push(item.endpointId);
          devicesDetails.push({
            type: device.type,
            endpointId: item.endpointId,
          });

          if (!Array.isArray(item.indicators)) {
            return;
          }

          item.indicators.forEach(indicator => {
            if (!uniqueIndicatorsFromDevices.hasOwnProperty(indicator.indicatorId)) {
              uniqueIndicatorsFromDevices[indicator.indicatorId] = indicator.occurrences;

              return;
            }

            uniqueIndicatorsFromDevices[indicator.indicatorId] += indicator.occurrences;
          });
        });
      });
    }

    return {
      devicesDetails,
      endpointIdsFromDevices,
      uniqueIndicatorsFromDevices,
    };
  }

  /**
   *
   * @param component
   * @param endpointIdsFromDevices
   * @param uniqueIndicatorsFromDevices
   * @private
   */
  private async getRemoteExtraDetailsForSidePanel(component, endpointIdsFromDevices, uniqueIndicatorsFromDevices) {
    const indicatorsIds = Object.keys(uniqueIndicatorsFromDevices);
    let deviceRemoteDetails: any = [];
    let indicatorsRemoteDetails: any = [];
    let deviceDetailsResultsPromise, indicatorsResultsPromise;

    const hasIndicators = indicatorsIds.length !== 0;
    const hasEndpointIds = endpointIdsFromDevices.length !== 0;

    if (hasEndpointIds) {
      deviceDetailsResultsPromise = ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'RiskManagementUsers',
        method: 'getDevicesNames',
        payload: {
          companyId: component.item.company._id,
          endpointIds: endpointIdsFromDevices,
        },
      });
    }

    if (hasIndicators) {
      indicatorsResultsPromise = ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'UserBehaviorRisks',
        method: 'getHumanBehaviorRisksForUser',
        payload: {
          companyId: component.item.company._id,
          ids: indicatorsIds,
          userId: component.item.uniqueIdentifier,
        },
      });
    }

    if (hasEndpointIds && hasIndicators) {
      const [deviceRemoteDetailsResponse, indicatorsRemoteDetailsResponse] = await Promise.allSettled([
        deviceDetailsResultsPromise,
        indicatorsResultsPromise,
      ]);

      if (deviceRemoteDetailsResponse.status === FULFILLED_PROMISE_STATUS) {
        deviceRemoteDetails = deviceRemoteDetailsResponse.value;
      }

      if (indicatorsRemoteDetailsResponse.status === FULFILLED_PROMISE_STATUS) {
        indicatorsRemoteDetails = indicatorsRemoteDetailsResponse.value;
      }
    } else if (hasEndpointIds) {
      deviceRemoteDetails = await deviceDetailsResultsPromise;
    } else if (hasIndicators) {
      indicatorsRemoteDetails = await indicatorsResultsPromise;
    }

    return {
      deviceRemoteDetails,
      indicatorsRemoteDetails,
    };
  }

  private async handleViewOtherUsersByRiskEvent(event: CustomEvent) {
    const params = {
      filters: [
        {
          [ColumnKeys.RISKS]: {
            complexEnumFilter: [{ value: event.detail.indicatorId }],
          },
        },
        {
          [ColumnKeys.COMPANY]: {
            exactMatch: event.detail.companyId,
          },
        },
      ],
    };

    NavigationHelper.navigateTo('/risk-management-identities', params);
  }

  private async handleViewHumanRisksByRisksEvent(event: CustomEvent) {
    const processedIndicatorIds = event.detail.indicatorIds.map(indicatorId => {
      return {
        value: indicatorId,
      };
    });

    const params = {
      filters: [
        {
          [UserBehaviorRisksColumnKeys.USER_BEHAVIOR_RISK]: {
            complexEnumFilter: processedIndicatorIds,
          },
        },
        {
          [ColumnKeys.COMPANY]: {
            exactMatch: event.detail.companyId,
          },
        },
      ],
      openSideMenuForFirstItem: processedIndicatorIds.length === 1,
    };

    NavigationHelper.navigateTo('/risk-management-identity-risks', params);
  }

  private async handleViewHumanRisksByUsersEvent(event: CustomEvent) {
    const processedUsers = event.detail.users.map(user => {
      return {
        label: user.username,
        value: user.uniqueIdentifier,
      };
    });

    const params = {
      filters: [
        {
          [UserBehaviorRisksColumnKeys.AFFECTED_USERS]: {
            complexEnumFilter: processedUsers,
          },
        },
        {
          [ColumnKeys.COMPANY]: {
            exactMatch: event.detail.companyId,
          },
        },
      ],
    };

    NavigationHelper.navigateTo('/risk-management-identity-risks', params);
  }

  private async handleDropDownViewIncidentsEvent(event: CustomEvent) {
    if (event.type === DropDownSelectEvents.viewIncidents) {
      Application.lib.Navigation.openInNewTab(`/incidents/filter/entity/${event.detail.endpointId}`);
    }
  }

  private async handleDropDownViewEventsEvent(event: CustomEvent) {
    if (event.type === DropDownSelectEvents.viewEvents) {
      Application.lib.Navigation.openInNewTab(`/xdr/device/${event.detail.endpointId}`);
    }
  }

  private async handleViewIncidentsByUser(event: CustomEvent) {
    if (event.type === RiskManagementSideMenuEvents.VIEW_INCIDENTS_BY_USER) {
      Application.lib.Navigation.openInNewTab(
        `/incidents/filter/username/${event.detail.companyId}&${encodeURIComponent(event.detail.username)}`
      );
    }
  }

  private async handleViewEventsByUser(event: CustomEvent) {
    if (event.type === RiskManagementSideMenuEvents.VIEW_EVENTS_AND_ALERTS_BY_USER) {
      Application.lib.Navigation.openInNewTab(
        `/xdr/events/user/${event.detail.companyId}&${encodeURIComponent(event.detail.username)}`
      );
    }
  }

  private async handleWatchlistChangedEvent(event: CustomEvent) {
    const { newWatchlist, ids, companyId } = event.detail;
    await this.updateWatchlistStatus(newWatchlist, ids, companyId);
  }

  private handleIgnoreUsersEvent(event: CustomEvent) {
    const { usersIds, companyId } = event.detail;

    this.createIgnoreDialog(usersIds, companyId);
  }

  private handleRestoreUsersEvent(event: CustomEvent) {
    const { usersIds, companyId } = event.detail;

    this.createRestoreDialog(usersIds, companyId);
  }

  private handleCSVExportEvent(event: CustomEvent) {
    this.view.dom.$grid.controller?.updateCsvButtonState(
      GzButtonState.IS_LOADING,
      this.dependencies.translateFn('Change the current filter selection to generate a new export.')
    );

    // if you change something here, change in GridDataProvider.getSummary also
    let filters: any[] = convertGridFiltersToExtJsFilters(event.detail.filters);
    const complexEnumFilterColumns = [
      ColumnKeys.USERNAME,
      ColumnKeys.RISKS,
      ColumnKeys.DEPARTMENT,
      ColumnKeys.USER_DEVICES,
      ColumnKeys.REGION,
      ColumnKeys.RESOURCE_ID,
    ];
    filters = handleComplexEnumFilter(complexEnumFilterColumns, filters);

    const url = app.buildDownloadUrl('RiskManagementUsers', 'exportItemsAsCsv', null, 'CSV');
    return Application.lib.DownloadManager.download(url, { filters: filters }, true);
  }

  private async updateWatchlistStatus(newWatchlistStatus: number, indicatorsIds: Array<number>, companyId: string) {
    await updateWatchlist(
      {
        ids: indicatorsIds,
        companyId,
        newWatchlist: newWatchlistStatus,
      },
      this.view.dom.$grid,
      this.view.dom.$sideMenu,
      WEB_COMPONENT_USERS_SIDE_MENU,
      'RiskManagementUsers'
    );
  }

  private createIgnoreDialog(usersIds: Array<string>, companyId: string) {
    let data: {
      dialogTitle: any;
      confirmButtonText: any;
      dialogBodyMessage: any;
      toastMessages: { success: any; error: any };
    };

    if (usersIds.length === 1) {
      data = {
        dialogBodyMessage: `
        ${renderDialogText(
          translateFn(
            'The selected identity will not be included in the overall resources and company risk score calculation. You can revert this action at any time.'
          )
        )}`,
        dialogTitle: translateFn('Ignore identity?'),
        confirmButtonText: translateFn('Ignore identity'),
        toastMessages: {
          success: translateFn('Successfully ignored identity. Changes will apply shortly.'),
          error: translateFn('Failed to ignore identity.'),
        },
      };
    } else {
      data = {
        dialogBodyMessage: `
        ${renderDialogText(
          translateFn(
            'The selected identities will not be included in the overall resources and company risk score calculation. You can revert this action at any time.'
          )
        )}`,
        dialogTitle: translateFn('Ignore identities?'),
        confirmButtonText: translateFn('Ignore identities'),
        toastMessages: {
          success: translateFn('Identities have been ignored. Changes will apply shortly.'),
          error: translateFn('Failed to ignore identities.'),
        },
      };
    }

    const requestPayload = {
      parameters: {
        actionType: Actions.IGNORE_USERS,
        usersIds,
        companyId,
      },
    };
    createActionDialog(
      {
        dialogTitle: data.dialogTitle,
        confirmButtonText: data.confirmButtonText,
        dialogBodyMessage: data.dialogBodyMessage,
      },
      {
        grid: this.view.dom.$grid,
        sideMenu: this.view.dom.$sideMenu,
      },
      requestPayload,
      data.toastMessages
    );
  }

  private createRestoreDialog(usersIds: Array<string>, companyId: string) {
    let data: {
      dialogTitle: any;
      confirmButtonText: any;
      dialogBodyMessage: any;
      toastMessages?: { success: any; error: any };
    };

    if (usersIds.length === 1) {
      data = {
        dialogBodyMessage: `
      ${renderDialogText(
        translateFn(
          'The selected identity will now be included in the overall resources and company risk score calculation. You can revert this action at any time.'
        )
      )}`,
        dialogTitle: translateFn('Restore ignored identity?'),
        confirmButtonText: translateFn('Restore ignored identity'),
        toastMessages: {
          success: translateFn('Ignored identity has been restored. Changes will apply shortly.'),
          error: translateFn('Failed to restore ignored identity.'),
        },
      };
    } else {
      data = {
        dialogBodyMessage: `
      ${renderDialogText(
        translateFn(
          'The selected identities will now be included in the overall resources and company risk score calculation. You can revert this action at any time.'
        )
      )}`,
        dialogTitle: translateFn('Restore ignored identities?'),
        confirmButtonText: translateFn('Restore ignored identities'),
        toastMessages: {
          success: translateFn('Ignored identities have been restored. Changes will apply shortly.'),
          error: translateFn('Failed to restore ignored identities.'),
        },
      };
    }

    const requestPayload = {
      parameters: {
        actionType: Actions.RESTORE_USERS,
        usersIds,
        companyId,
      },
    };
    createActionDialog(
      {
        dialogTitle: data.dialogTitle,
        confirmButtonText: data.confirmButtonText,
        dialogBodyMessage: data.dialogBodyMessage,
      },
      {
        grid: this.view.dom.$grid,
        sideMenu: this.view.dom.$sideMenu,
      },
      requestPayload,
      data.toastMessages
    );
  }

  private async handleUserDetailsShowExtraDataEvent(event: CustomEvent) {
    const sideMenuComponent = this.view.dom.$sideMenu.querySelector(
      WEB_COMPONENT_USERS_SIDE_MENU
    ) as UsersSideMenuWebComponent;

    const details: ShowExtraDataEventDetails = event.detail;

    sideMenuComponent.showExtraDetailsPanel = details.showExtraDetailsPanel;

    if (details.extraDetailsPanelTitle) {
      sideMenuComponent.extraDetailsPanelTitle = details.extraDetailsPanelTitle;
    }

    sideMenuComponent.userExtraDetails = {
      items: details.items,
      itemsType: details.itemsType,
      companyId: details.companyId,
    };
  }

  private async handleAffectedResourceIconClickedEvent(event: CustomEvent) {
    const { iconType, companyId, affectedResourcesIds } = event.detail;

    const affectedResourcesSideMenu = buildCategoriesSideMenu(companyId, iconType, translateFn('Identity resources'));

    this.view.dom.$sideMenu.setMenuContent(affectedResourcesSideMenu);
    this.view.dom.$sideMenu.openSideMenu();

    let typeToItemsMapping = [];
    try {
      typeToItemsMapping = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'RiskManagementUsers',
        method: 'getAffectedResourcesDetails',
        payload: {
          companyId,
          affectedResourcesIds,
        },
      });
    } catch (error) {
      affectedResourcesSideMenu.hasErrors = true;
    }

    const categoriesForSidePanel = processAffectedDeviceData(typeToItemsMapping);
    affectedResourcesSideMenu.initialCategories = categoriesForSidePanel;
  }

  handleRightSideNavClosed() {
    removeActiveStateFromGzIconFromAffectedDevices(this.view);
  }
}
